// TEMPORARY — Zendesk cutover tool. Pulls Zendesk tickets/comments/attachments.
// Removed from the Admin landing page; route still wired in App.tsx for direct access.
// Delete this file, its route, and the backend endpoint together once cutover is done.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Failure {
  zendeskTicketId: number;
  subject: string;
  error: string;
  zendeskOrgId: number | null;
  zendeskOrgName: string | null;
  requesterEmail: string | null;
  suggestedT3OrgId: string | null;
  suggestedT3OrgName: string | null;
}

interface ImportResult {
  ticketsDeletedBeforeImport: number;
  ticketsScanned: number;
  imported: number;
  updated: number;
  skipped: number;
  commentsImported: number;
  attachmentsImported: number;
  failures: Failure[];
}

interface Status {
  configured: boolean;
  subdomain: string | null;
}

interface T3OrgOption {
  id: string;
  name: string;
}

// Per-failure dropdown choice. 'unset' is the default — operator hasn't
// picked yet, the row is excluded from the retry. Selecting an org id
// queues the mapping for the retry call.
type ChoiceValue = 'unset' | string;

export function AdminZendeskTicketImportPage(): React.ReactElement {
  useDocumentTitle('Import Zendesk tickets');
  const { agent } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  // Org list for the resolve-failures dropdowns. Lazy-loaded only when
  // there are failures to resolve, so the initial page paint stays cheap.
  const [t3Orgs, setT3Orgs] = useState<T3OrgOption[] | null>(null);

  // Operator choices for the retry call. Two modes:
  //   * byZendeskOrgChoice — bulk-by-Zendesk-org-id for the "org not in T3"
  //     group, so 60 tickets sharing a missing Zendesk org are mapped once.
  //   * byTicketChoice — per-ticket override (Zendesk ticket id → T3 org id).
  //     Used for orgless tickets so each one can go to a different T3 org,
  //     and as a per-row override for any "org not in T3" ticket that needs
  //     to differ from its group's bulk choice.
  // Backend resolution priority: byTicketChoice > byZendeskOrgChoice.
  const [byZendeskOrgChoice, setByZendeskOrgChoice] = useState<
    Record<string, ChoiceValue>
  >({});
  const [byTicketChoice, setByTicketChoice] = useState<
    Record<string, ChoiceValue>
  >({});

  useEffect(() => {
    apiFetch<Status>('/api/admin/zendesk-ticket-import/status')
      .then(setStatus)
      .catch(() => setStatus({ configured: false, subdomain: null }));
  }, []);

  // Load T3 orgs the first time a result with failures arrives. Re-uses the
  // cached list on subsequent retries.
  useEffect(() => {
    if (!result || result.failures.length === 0 || t3Orgs !== null) return;
    apiFetch<{ organizations: T3OrgOption[] }>('/api/organizations?status=all')
      .then((r) => setT3Orgs(r.organizations.map((o) => ({ id: o.id, name: o.name }))))
      .catch(() => setT3Orgs([]));
  }, [result, t3Orgs]);

  // Pre-seed dropdowns from suggestions when a new result lands. Both the
  // by-Zendesk-org bulk dropdowns and the per-ticket dropdowns get the
  // server's best guess; operator can override either.
  useEffect(() => {
    if (!result) return;
    const groupDefaults: Record<string, ChoiceValue> = {};
    const ticketDefaults: Record<string, ChoiceValue> = {};
    for (const f of result.failures) {
      ticketDefaults[String(f.zendeskTicketId)] = f.suggestedT3OrgId ?? 'unset';
      if (f.zendeskOrgId !== null) {
        const k = String(f.zendeskOrgId);
        if (!(k in groupDefaults)) {
          groupDefaults[k] = f.suggestedT3OrgId ?? 'unset';
        }
      }
    }
    setByZendeskOrgChoice(groupDefaults);
    setByTicketChoice(ticketDefaults);
  }, [result]);

  // Group failures: by Zendesk org id (for the "org not in T3" case) and
  // a single bucket for the "no org in Zendesk" case.
  const grouped = useMemo(() => {
    if (!result) return { byOrg: new Map<number, { name: string | null; rows: Failure[] }>(), noOrg: [] as Failure[] };
    const byOrg = new Map<number, { name: string | null; rows: Failure[] }>();
    const noOrg: Failure[] = [];
    for (const f of result.failures) {
      if (f.zendeskOrgId !== null) {
        const existing = byOrg.get(f.zendeskOrgId);
        if (existing) existing.rows.push(f);
        else byOrg.set(f.zendeskOrgId, { name: f.zendeskOrgName, rows: [f] });
      } else {
        noOrg.push(f);
      }
    }
    return { byOrg, noOrg };
  }, [result]);

  async function run(body?: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<ImportResult>('/api/admin/zendesk-ticket-import/tickets', {
        method: 'POST',
        json: body ?? {},
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  async function retryWithMappings() {
    const byZendeskOrgId: Record<string, string> = {};
    for (const [k, v] of Object.entries(byZendeskOrgChoice)) {
      if (v !== 'unset') byZendeskOrgId[k] = v;
    }
    // Only include per-ticket choices that differ from what the group
    // already covers. Sending a per-ticket override that matches the
    // group's choice is harmless (server picks per-ticket first either
    // way) but trims the payload and makes the wire intent clearer.
    const byZendeskTicketId: Record<string, string> = {};
    if (result) {
      for (const f of result.failures) {
        const tk = String(f.zendeskTicketId);
        const choice = byTicketChoice[tk] ?? 'unset';
        if (choice === 'unset') continue;
        const groupChoice =
          f.zendeskOrgId !== null
            ? byZendeskOrgChoice[String(f.zendeskOrgId)] ?? 'unset'
            : 'unset';
        if (choice !== groupChoice) byZendeskTicketId[tk] = choice;
      }
    }
    const haveMappings =
      Object.keys(byZendeskOrgId).length > 0 ||
      Object.keys(byZendeskTicketId).length > 0;
    if (!haveMappings) {
      setError('Pick a T3 org for at least one row before retrying.');
      return;
    }
    await run({
      orgOverrides: { byZendeskOrgId, byZendeskTicketId },
    });
  }

  const hasFailures = result && result.failures.length > 0;
  const sortedOrgs = useMemo(
    () => (t3Orgs ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [t3Orgs],
  );

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">
          Import Zendesk tickets
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Pulls tickets, comments, and attachments — all statuses, capped at 50,000.
        </p>
      </div>

      <Card className="border-red-300 bg-red-50/40">
        <h2 className="text-sm font-semibold text-red-900">⚠ Destructive — every run wipes the ticket table first</h2>
        <p className="mt-2 text-sm text-red-900">
          To guarantee a clean, orphan-free import, every run begins by{' '}
          <strong>deleting every ticket in T3</strong> and everything FK-attached to it:
          ticket messages, attachments, time entries (including any imported from Harvest),
          active timers, CSAT surveys, and the Zendesk ticket map. Projects survive but lose
          any link to a quote ticket. Notifications keep their text but lose their ticket
          link. Re-import time entries from Harvest after this completes if needed.
        </p>
      </Card>

      <Card className="border-amber-300 bg-amber-50/40">
        <h2 className="text-sm font-semibold text-amber-900">Run these first</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900">
          <li>
            <Link to="/admin/import-zendesk-orgs" className="underline">
              Import organizations + contacts
            </Link>{' '}
            — tickets without a matching T3 org show up below for manual mapping after the import.
          </li>
          <li>
            Make sure your Zendesk agents exist as T3 agents with the same email — the importer
            matches them by email to set the assignee. Missing matches result in unassigned tickets.
          </li>
        </ul>
      </Card>

      {status && !status.configured && (
        <Card className="border-amber-300 bg-amber-50/40">
          <p className="text-sm text-amber-900">
            Zendesk is not configured. Set <code>ZENDESK_SUBDOMAIN</code>,{' '}
            <code>ZENDESK_EMAIL</code>, and <code>ZENDESK_API_TOKEN</code> in your environment,
            then restart the backend.
          </p>
        </Card>
      )}

      {status && status.configured && (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            Connected to{' '}
            <code className="text-[var(--color-body)]">{status.subdomain}.zendesk.com</code>.
          </p>
          {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}
          <div className="mt-3">
            <Button onClick={() => void run()} disabled={busy}>
              {busy ? 'Importing… (can take several minutes)' : 'Import tickets'}
            </Button>
          </div>
        </Card>
      )}

      {result && (
        <Card className="border-green-300 bg-green-50/40">
          <h2 className="text-base font-semibold text-green-900">Import complete</h2>
          <ul className="mt-2 space-y-1 text-sm text-green-900">
            <li>Tickets deleted before import: <strong>{result.ticketsDeletedBeforeImport}</strong></li>
            <li>Tickets scanned: {result.ticketsScanned}</li>
            <li>New tickets imported: <strong>{result.imported}</strong></li>
            <li>Existing tickets overwritten: <strong>{result.updated}</strong></li>
            <li>Skipped: {result.skipped}</li>
            <li>Comments imported: {result.commentsImported}</li>
            <li>Attachments imported: {result.attachmentsImported}</li>
          </ul>
          {result.failures.length === 0 && (
            <p className="mt-3 text-sm text-green-900">No failures. Nice.</p>
          )}
          <div className="mt-3">
            <Button variant="outline" onClick={() => navigate('/tickets')}>
              View tickets
            </Button>
          </div>
        </Card>
      )}

      {hasFailures && (
        <Card className="border-[var(--color-danger)]/40 bg-red-50/30">
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            Resolve failures ({result.failures.length})
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Pick a T3 org for each group below, then "Apply mappings & retry." Suggested
            matches are pre-filled when we found a likely org by name or by the requester's
            email domain — change them if they're wrong. Anything you leave as
            <em> Choose…</em> stays unimported. Already-imported tickets are skipped on retry, so
            this is safe to re-run.
          </p>

          {grouped.byOrg.size > 0 && (
            <section className="mt-4 space-y-3">
              <h3 className="text-sm font-semibold text-[var(--color-header)]">
                Zendesk org not found in T3
              </h3>
              {[...grouped.byOrg.entries()].map(([zendeskOrgId, group]) => {
                const choice = byZendeskOrgChoice[String(zendeskOrgId)] ?? 'unset';
                const suggestedName = group.rows.find((r) => r.suggestedT3OrgName)?.suggestedT3OrgName;
                return (
                  <div
                    key={zendeskOrgId}
                    className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div>
                        <span className="font-medium text-[var(--color-body)]">
                          {group.name ?? `Zendesk org #${zendeskOrgId}`}
                        </span>
                        <span className="ml-2 font-mono text-xs text-[var(--color-muted)]">
                          Zendesk id {zendeskOrgId} · {group.rows.length} ticket
                          {group.rows.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {suggestedName && choice === 'unset' && (
                        <span className="text-xs text-emerald-700">
                          Suggested: <strong>{suggestedName}</strong>
                        </span>
                      )}
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-[var(--color-muted)]">
                        Map to T3 org
                      </label>
                      <select
                        className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2 py-1 text-sm"
                        value={choice}
                        onChange={(e) =>
                          setByZendeskOrgChoice((prev) => ({
                            ...prev,
                            [String(zendeskOrgId)]: e.target.value as ChoiceValue,
                          }))
                        }
                      >
                        <option value="unset">Choose…</option>
                        {sortedOrgs.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-[var(--color-t3-blue)] hover:underline">
                        Show {group.rows.length} affected ticket
                        {group.rows.length === 1 ? '' : 's'}
                      </summary>
                      <ul className="mt-2 space-y-1 text-xs">
                        {group.rows.map((r) => (
                          <li
                            key={r.zendeskTicketId}
                            className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1"
                          >
                            <span className="font-mono text-[var(--color-muted)]">
                              #{r.zendeskTicketId}
                            </span>{' '}
                            <span className="text-[var(--color-body)]">{r.subject}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                );
              })}
            </section>
          )}

          {grouped.noOrg.length > 0 && (
            <section className="mt-4 space-y-3">
              <h3 className="text-sm font-semibold text-[var(--color-header)]">
                Ticket has no organization in Zendesk ({grouped.noOrg.length})
              </h3>
              <p className="text-xs text-[var(--color-muted)]">
                These tickets had no client attached in Zendesk — onboarding emails, internal
                notes, password resets, vendor threads. Pick a T3 org for each one individually,
                or use the bulk control to set them all at once. Tickets left as
                <em> Choose…</em> stay unimported.
              </p>

              {/* Bulk set-all control. Convenience for the "stamp them all
                  with T3 Internal" case; per-row dropdowns can still override. */}
              <div className="flex flex-wrap items-end gap-2 rounded-[var(--radius-button)] border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-2">
                <div className="flex-1 min-w-[12rem]">
                  <label className="block text-xs font-medium text-[var(--color-muted)]">
                    Bulk: set all orgless tickets to
                  </label>
                  <select
                    className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2 py-1 text-sm"
                    value="unset"
                    onChange={(e) => {
                      const v = e.target.value as ChoiceValue;
                      if (v === 'unset') return;
                      setByTicketChoice((prev) => {
                        const next = { ...prev };
                        for (const r of grouped.noOrg) {
                          next[String(r.zendeskTicketId)] = v;
                        }
                        return next;
                      });
                    }}
                  >
                    <option value="unset">Choose to bulk-set…</option>
                    {sortedOrgs.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                {grouped.noOrg.map((r) => {
                  const tk = String(r.zendeskTicketId);
                  const choice = byTicketChoice[tk] ?? 'unset';
                  return (
                    <div
                      key={r.zendeskTicketId}
                      className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-mono text-xs text-[var(--color-muted)]">
                            Zendesk #{r.zendeskTicketId}
                          </span>{' '}
                          <span className="text-sm text-[var(--color-body)]">{r.subject}</span>
                        </div>
                        {r.suggestedT3OrgName && choice === 'unset' && (
                          <span className="text-xs text-emerald-700">
                            Suggested: <strong>{r.suggestedT3OrgName}</strong>
                          </span>
                        )}
                      </div>
                      {r.requesterEmail && (
                        <p className="mt-1 text-xs text-[var(--color-muted)]">
                          from {r.requesterEmail}
                        </p>
                      )}
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-[var(--color-muted)]">
                          Map to T3 org
                        </label>
                        <select
                          className="mt-1 w-full rounded border border-[var(--color-border)] bg-white px-2 py-1 text-sm"
                          value={choice}
                          onChange={(e) =>
                            setByTicketChoice((prev) => ({
                              ...prev,
                              [tk]: e.target.value as ChoiceValue,
                            }))
                          }
                        >
                          <option value="unset">Choose…</option>
                          {sortedOrgs.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <div className="mt-4 flex gap-2">
            <Button onClick={() => void retryWithMappings()} disabled={busy}>
              {busy ? 'Retrying…' : 'Apply mappings & retry'}
            </Button>
            <details className="ml-auto text-sm">
              <summary className="cursor-pointer text-[var(--color-muted)] hover:underline">
                Show raw failure list
              </summary>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                {result.failures.map((f) => (
                  <li key={f.zendeskTicketId}>
                    <strong>Zendesk #{f.zendeskTicketId}</strong> ({f.subject}): {f.error}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        </Card>
      )}
    </div>
  );
}
